import { useState, useEffect, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Trash2,
  Copy,
  Check,
  Send,
  Music,
  Zap,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

interface SetlistTrack {
  id: string
  position: number
  title: string
  durationSeconds: number | null
  key: string | null
  bpm: number | null
  hasBackingTrack: boolean
  backingTrackRef: string | null
  instrumentChanges: string | null
  lightingCue: string | null
  videoCue: string | null
  notesToBand: string | null
  notesToCrew: string | null
  energyLevel: number | null
  isEncore: boolean
  isOptional: boolean
}

interface SetlistRow {
  id: string
  name: string
  version: number
  isPublished: boolean
  tourId: string | null
  legId: string | null
  showId: string | null
  parentId: string | null
  notes: string | null
  tracks: SetlistTrack[]
  createdAt: string
  updatedAt: string
  show?: { id: string; title: string; date: string; venue: string } | null
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '--:--'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function totalRuntime(tracks: SetlistTrack[]): string {
  const total = tracks.reduce((acc, t) => acc + (t.durationSeconds ?? 0), 0)
  if (total === 0) return '0:00'
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}:${String(s).padStart(2, '0')}`
}

function EnergyBar({ level }: { level: number | null }) {
  if (!level) return <span className="text-xs text-ovation-muted">--</span>
  const colors = ['bg-blue-300', 'bg-green-300', 'bg-yellow-300', 'bg-orange-400', 'bg-red-500']
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`h-3 w-1.5 rounded-sm ${i <= level ? colors[level - 1] : 'bg-ovation-ink/10'}`}
        />
      ))}
    </div>
  )
}

function EnergyArc({ tracks }: { tracks: SetlistTrack[] }) {
  const sorted = [...tracks].sort((a, b) => a.position - b.position)
  if (sorted.length === 0) return null
  const maxH = 40
  return (
    <div className="mt-4 rounded-lg border border-ovation-ink/5 bg-ovation-ink/[0.02] p-3">
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ovation-muted">Show Energy Arc</p>
      <div className="flex items-end gap-1" style={{ height: maxH }}>
        {sorted.map((t) => {
          const energy = t.energyLevel ?? 1
          const h = (energy / 5) * maxH
          const colors = ['bg-blue-300', 'bg-green-300', 'bg-yellow-300', 'bg-orange-400', 'bg-red-500']
          return (
            <div
              key={t.id}
              title={`${t.position}. ${t.title} (energy ${energy})`}
              className={`flex-1 rounded-t-sm ${t.isEncore ? 'opacity-60 border-l-2 border-dashed border-ovation-ink/20' : ''} ${colors[energy - 1]}`}
              style={{ height: h }}
            />
          )
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-ovation-muted">
        <span>Opener</span>
        <span>Closer</span>
      </div>
    </div>
  )
}

export function SetlistPage() {
  const qc = useQueryClient()
  const scope = usePerformanceScope()
  const [adding, setAdding] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [editingTrack, setEditingTrack] = useState<string | null>(null)
  const [filterTourId, setFilterTourId] = useState('')
  const [filterLegId, setFilterLegId] = useState('')
  const [filterShowId, setFilterShowId] = useState('')

  useEffect(() => {
    if (!scope.active) return
    if (scope.kind === 'tour') {
      setFilterTourId(scope.id!)
      setFilterLegId('')
      setFilterShowId('')
    } else if (scope.kind === 'show') {
      setFilterShowId(scope.id!)
      setFilterTourId('')
      setFilterLegId('')
    }
  }, [scope.active, scope.kind, scope.id])

  const toursQuery = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['tours'],
    queryFn: () => artistFetch('/tours'),
  })
  const tours = toursQuery.data ?? []

  const filterLegsQuery = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['tours', filterTourId, 'legs'],
    queryFn: () => artistFetch(`/tours/${filterTourId}/legs`),
    enabled: !!filterTourId,
  })
  const filterLegs = filterLegsQuery.data ?? []

  const showsQuery = useQuery<Array<{ id: string; title: string; date: string; venue: string; city: string }>>({
    queryKey: ['shows'],
    queryFn: () => artistFetch('/shows'),
  })
  const shows = showsQuery.data ?? []

  const queryParams = new URLSearchParams()
  if (filterTourId) queryParams.set('tourId', filterTourId)
  if (filterLegId) queryParams.set('legId', filterLegId)
  if (filterShowId) queryParams.set('showId', filterShowId)

  const { data, error, isLoading } = useQuery<{ data: SetlistRow[]; meta: any }, ApiClientError>({
    queryKey: ['setlists', filterTourId, filterLegId, filterShowId],
    queryFn: () => artistFetch(`/setlists?${queryParams.toString()}`),
  })

  const setlists = data?.data ?? []

  const publishMutation = useMutation({
    mutationFn: (id: string) => artistFetch(`/setlists/${id}/publish`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['setlists'] }),
  })

  const versionMutation = useMutation({
    mutationFn: (id: string) => artistFetch(`/setlists/${id}/version`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['setlists'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => artistFetch(`/setlists/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['setlists'] }),
  })

  async function moveTrack(setlistId: string, tracks: SetlistTrack[], trackId: string, direction: 'up' | 'down') {
    const sorted = [...tracks].sort((a, b) => a.position - b.position)
    const idx = sorted.findIndex((t) => t.id === trackId)
    if (direction === 'up' && idx === 0) return
    if (direction === 'down' && idx === sorted.length - 1) return
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    const reordered = [...sorted]
    const temp = reordered[idx]!
    reordered[idx] = reordered[swapIdx]!
    reordered[swapIdx] = temp
    const trackIds = reordered.map((t) => t.id)
    await artistFetch(`/setlists/${setlistId}/tracks/reorder`, {
      method: 'POST',
      body: { trackIds },
    })
    qc.invalidateQueries({ queryKey: ['setlists'] })
  }

  async function removeTrack(setlistId: string, trackId: string) {
    await artistFetch(`/setlists/${setlistId}/tracks/${trackId}`, { method: 'DELETE' })
    qc.invalidateQueries({ queryKey: ['setlists'] })
  }

  return (
    <div>
      <PageHeader
        title="Setlists"
        subtitle="Version-controlled setlists with production cues and energy mapping."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'New setlist'}
          </button>
        }
      />

      <PerformanceScopeBanner />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <select
          value={filterTourId}
          onChange={(e) => {
            setFilterTourId(e.target.value)
            setFilterLegId('')
          }}
          className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
        >
          <option value="">All tours</option>
          {tours.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {filterTourId && (
          <select
            value={filterLegId}
            onChange={(e) => setFilterLegId(e.target.value)}
            className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
          >
            <option value="">All legs</option>
            {filterLegs.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        )}
        <select
          value={filterShowId}
          onChange={(e) => setFilterShowId(e.target.value)}
          className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
        >
          <option value="">All shows</option>
          {shows.map((s) => (
            <option key={s.id} value={s.id}>
              {new Date(s.date).toLocaleDateString()} — {s.title} ({s.venue}, {s.city})
            </option>
          ))}
        </select>
      </div>

      {adding && (
        <AddSetlistForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['setlists'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        <div className="space-y-3">
          {setlists.length === 0 && (
            <p className="py-8 text-center text-sm text-ovation-muted">No setlists created yet.</p>
          )}
          {setlists.map((setlist) => (
            <div key={setlist.id} className="rounded-lg border border-ovation-ink/10 bg-white">
              <div
                className="flex cursor-pointer items-center gap-3 px-4 py-3"
                onClick={() => setExpanded(expanded === setlist.id ? null : setlist.id)}
              >
                {expanded === setlist.id ? (
                  <ChevronDown className="h-4 w-4 text-ovation-muted" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-ovation-muted" />
                )}
                <Music className="h-4 w-4 text-ovation-muted" />
                <div className="flex-1">
                  <span className="font-medium">{setlist.name}</span>
                  <span className="ml-2 text-xs text-ovation-muted">v{setlist.version}</span>
                  {setlist.show && (
                    <span className="ml-2 text-xs text-ovation-muted">
                      {setlist.show.title} - {setlist.show.venue}
                    </span>
                  )}
                  <span className="ml-2 text-xs text-ovation-muted">
                    {setlist.tracks.length} tracks | {totalRuntime(setlist.tracks)}
                  </span>
                </div>
                {setlist.isPublished && (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                    Published
                  </span>
                )}
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  {!setlist.isPublished && (
                    <button
                      type="button"
                      onClick={() => publishMutation.mutate(setlist.id)}
                      className="rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
                      title="Publish"
                    >
                      <Send className="h-3 w-3" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => versionMutation.mutate(setlist.id)}
                    className="rounded px-2 py-1 text-xs text-ovation-muted hover:bg-ovation-ink/5"
                    title="Create new version"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(setlist.id)}
                    className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {expanded === setlist.id && (
                <div className="border-t border-ovation-ink/5 px-4 py-3">
                  {setlist.notes && (
                    <p className="mb-3 text-sm text-ovation-muted italic">{setlist.notes}</p>
                  )}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-ovation-muted">
                        <th className="pb-2 pr-2 w-8">#</th>
                        <th className="pb-2 pr-2">Title</th>
                        <th className="pb-2 pr-2 w-16">Time</th>
                        <th className="pb-2 pr-2 w-20">Key</th>
                        <th className="pb-2 pr-2 w-14">BPM</th>
                        <th className="pb-2 pr-2 w-16">Energy</th>
                        <th className="pb-2 pr-2 w-24">Lighting</th>
                        <th className="pb-2 pr-2 w-24">Video</th>
                        <th className="pb-2 w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...setlist.tracks]
                        .sort((a, b) => a.position - b.position)
                        .map((track) => (
                          <tr
                            key={track.id}
                            className={`border-t border-ovation-ink/5 ${track.isEncore ? 'bg-purple-50/50' : ''} ${track.isOptional ? 'opacity-60' : ''}`}
                          >
                            <td className="py-1.5 pr-2 text-ovation-muted">{track.position}</td>
                            <td className="py-1.5 pr-2">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">{track.title}</span>
                                {track.isEncore && (
                                  <span className="rounded bg-purple-100 px-1 text-[10px] font-medium text-purple-700">ENC</span>
                                )}
                                {track.isOptional && (
                                  <span className="rounded bg-gray-100 px-1 text-[10px] font-medium text-gray-600">OPT</span>
                                )}
                                {track.hasBackingTrack && (
                                  <span className="rounded bg-blue-100 px-1 text-[10px] font-medium text-blue-700">BT</span>
                                )}
                              </div>
                            </td>
                            <td className="py-1.5 pr-2 text-ovation-muted">
                              {formatDuration(track.durationSeconds)}
                            </td>
                            <td className="py-1.5 pr-2 text-ovation-muted">{track.key ?? '--'}</td>
                            <td className="py-1.5 pr-2 text-ovation-muted">{track.bpm ?? '--'}</td>
                            <td className="py-1.5 pr-2">
                              <EnergyBar level={track.energyLevel} />
                            </td>
                            <td className="py-1.5 pr-2 text-xs text-ovation-muted truncate max-w-[96px]">
                              {track.lightingCue ?? '--'}
                            </td>
                            <td className="py-1.5 pr-2 text-xs text-ovation-muted truncate max-w-[96px]">
                              {track.videoCue ?? '--'}
                            </td>
                            <td className="py-1.5">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveTrack(setlist.id, setlist.tracks, track.id, 'up')}
                                  className="rounded p-0.5 hover:bg-ovation-ink/5"
                                >
                                  <ArrowUp className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveTrack(setlist.id, setlist.tracks, track.id, 'down')}
                                  className="rounded p-0.5 hover:bg-ovation-ink/5"
                                >
                                  <ArrowDown className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingTrack(editingTrack === track.id ? null : track.id)}
                                  className="rounded p-0.5 hover:bg-ovation-ink/5"
                                  title="Edit details"
                                >
                                  <Zap className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeTrack(setlist.id, track.id)}
                                  className="rounded p-0.5 text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>

                  {/* Inline track edit */}
                  {editingTrack && (
                    <TrackEditInline
                      setlistId={setlist.id}
                      track={setlist.tracks.find((t) => t.id === editingTrack)!}
                      onClose={() => setEditingTrack(null)}
                    />
                  )}

                  <AddTrackInline setlistId={setlist.id} trackCount={setlist.tracks.length} />
                  <EnergyArc tracks={setlist.tracks} />
                </div>
              )}
            </div>
          ))}
        </div>
      </AsyncBoundary>
    </div>
  )
}

function TrackEditInline({
  setlistId,
  track,
  onClose,
}: {
  setlistId: string
  track: SetlistTrack
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    title: track.title,
    durationSeconds: track.durationSeconds?.toString() ?? '',
    key: track.key ?? '',
    bpm: track.bpm?.toString() ?? '',
    energyLevel: track.energyLevel?.toString() ?? '',
    lightingCue: track.lightingCue ?? '',
    videoCue: track.videoCue ?? '',
    instrumentChanges: track.instrumentChanges ?? '',
    notesToBand: track.notesToBand ?? '',
    notesToCrew: track.notesToCrew ?? '',
    hasBackingTrack: track.hasBackingTrack,
    backingTrackRef: track.backingTrackRef ?? '',
    isEncore: track.isEncore,
    isOptional: track.isOptional,
  })

  async function save(e: FormEvent) {
    e.preventDefault()
    await artistFetch(`/setlists/${setlistId}/tracks/${track.id}`, {
      method: 'PATCH',
      body: {
        title: form.title,
        durationSeconds: form.durationSeconds ? Number(form.durationSeconds) : null,
        key: form.key || null,
        bpm: form.bpm ? Number(form.bpm) : null,
        energyLevel: form.energyLevel ? Number(form.energyLevel) : null,
        lightingCue: form.lightingCue || null,
        videoCue: form.videoCue || null,
        instrumentChanges: form.instrumentChanges || null,
        notesToBand: form.notesToBand || null,
        notesToCrew: form.notesToCrew || null,
        hasBackingTrack: form.hasBackingTrack,
        backingTrackRef: form.backingTrackRef || null,
        isEncore: form.isEncore,
        isOptional: form.isOptional,
      },
    })
    qc.invalidateQueries({ queryKey: ['setlists'] })
    onClose()
  }

  return (
    <form onSubmit={save} className="mt-3 rounded-lg border border-blue-200 bg-blue-50/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-ovation-muted">
          Editing: {track.title}
        </p>
        <button type="button" onClick={onClose} className="text-xs text-ovation-muted hover:text-ovation-ink">
          Close
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Title</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Duration (sec)</label>
          <input type="number" value={form.durationSeconds} onChange={(e) => setForm({ ...form, durationSeconds: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Key</label>
          <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" placeholder="e.g. Bb Major" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">BPM</label>
          <input type="number" value={form.bpm} onChange={(e) => setForm({ ...form, bpm: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Energy (1-5)</label>
          <input type="number" min={1} max={5} value={form.energyLevel} onChange={(e) => setForm({ ...form, energyLevel: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Lighting Cue</label>
          <input value={form.lightingCue} onChange={(e) => setForm({ ...form, lightingCue: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Video Cue</label>
          <input value={form.videoCue} onChange={(e) => setForm({ ...form, videoCue: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Backing Track Ref</label>
          <input value={form.backingTrackRef} onChange={(e) => setForm({ ...form, backingTrackRef: e.target.value })} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Instrument Changes</label>
          <textarea value={form.instrumentChanges} onChange={(e) => setForm({ ...form, instrumentChanges: e.target.value })} rows={2} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Notes to Band</label>
          <textarea value={form.notesToBand} onChange={(e) => setForm({ ...form, notesToBand: e.target.value })} rows={2} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Notes to Crew</label>
          <textarea value={form.notesToCrew} onChange={(e) => setForm({ ...form, notesToCrew: e.target.value })} rows={2} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={form.hasBackingTrack} onChange={(e) => setForm({ ...form, hasBackingTrack: e.target.checked })} />
          Backing track
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={form.isEncore} onChange={(e) => setForm({ ...form, isEncore: e.target.checked })} />
          Encore
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={form.isOptional} onChange={(e) => setForm({ ...form, isOptional: e.target.checked })} />
          Optional (droppable)
        </label>
      </div>
      <button type="submit" className="rounded-md bg-ovation-ink px-4 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90">
        Save changes
      </button>
    </form>
  )
}

function AddTrackInline({ setlistId, trackCount }: { setlistId: string; trackCount: number }) {
  const qc = useQueryClient()
  const [title, setTitle] = useState('')
  const [duration, setDuration] = useState('')
  const [energy, setEnergy] = useState('')

  async function add(e: FormEvent) {
    e.preventDefault()
    if (!title) return
    await artistFetch(`/setlists/${setlistId}/tracks`, {
      method: 'POST',
      body: {
        position: trackCount + 1,
        title,
        ...(duration ? { durationSeconds: Number(duration) } : {}),
        ...(energy ? { energyLevel: Number(energy) } : {}),
      },
    })
    setTitle('')
    setDuration('')
    setEnergy('')
    qc.invalidateQueries({ queryKey: ['setlists'] })
  }

  return (
    <form onSubmit={add} className="mt-3 flex items-center gap-2">
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Add song title..."
        className="flex-1 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm"
      />
      <input
        type="number"
        value={duration}
        onChange={(e) => setDuration(e.target.value)}
        placeholder="Sec"
        className="w-20 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm"
      />
      <input
        type="number"
        min={1}
        max={5}
        value={energy}
        onChange={(e) => setEnergy(e.target.value)}
        placeholder="1-5"
        className="w-16 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm"
      />
      <button type="submit" className="rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90">
        Add
      </button>
    </form>
  )
}

function AddSetlistForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('')
  const [tourId, setTourId] = useState('')
  const [legId, setLegId] = useState('')
  const [showId, setShowId] = useState('')
  const [notes, setNotes] = useState('')
  const [tracksText, setTracksText] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const toursQuery = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['tours'],
    queryFn: () => artistFetch('/tours'),
  })
  const tours = toursQuery.data ?? []

  const legsQuery = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['tours', tourId, 'legs'],
    queryFn: () => artistFetch(`/tours/${tourId}/legs`),
    enabled: !!tourId,
  })
  const legs = legsQuery.data ?? []

  const showsQuery = useQuery<Array<{ id: string; title: string; date: string; venue: string; city: string }>>({
    queryKey: ['shows'],
    queryFn: () => artistFetch('/shows'),
  })
  const shows = showsQuery.data ?? []

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const lines = tracksText.split('\n').filter((l) => l.trim())
      const tracks = lines.map((line, i) => ({ position: i + 1, title: line.trim() }))
      if (tracks.length === 0) {
        setErr('Add at least one track (one per line)')
        setSubmitting(false)
        return
      }
      await artistFetch('/setlists', {
        method: 'POST',
        body: {
          name,
          tracks,
          ...(tourId ? { tourId } : {}),
          ...(legId ? { legId } : {}),
          ...(showId ? { showId } : {}),
          ...(notes ? { notes } : {}),
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="e.g. Legacy Tour Setlist" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Tour (optional)</label>
          <select
            value={tourId}
            onChange={(e) => {
              setTourId(e.target.value)
              setLegId('')
            }}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">— None —</option>
            {tours.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        {tourId && (
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Leg (optional)</label>
            <select
              value={legId}
              onChange={(e) => setLegId(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            >
              <option value="">— None —</option>
              {legs.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show (optional)</label>
          <select
            value={showId}
            onChange={(e) => setShowId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">— None —</option>
            {shows.map((s) => (
              <option key={s.id} value={s.id}>
                {new Date(s.date).toLocaleDateString()} — {s.title} ({s.venue}, {s.city})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Notes (optional)</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Internal notes" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Tracks (one per line)</label>
        <textarea
          required
          value={tracksText}
          onChange={(e) => setTracksText(e.target.value)}
          rows={6}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          placeholder={"Cruisin'\nBeing with You\nTears of a Clown"}
        />
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : 'Create setlist'}
      </button>
    </form>
  )
}
